import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { RunType } from '@/enums/run-type.enum';
import { log } from '@/logger/logger';
import { fileMatchesAcceptedCategory } from '@/utils/fileTypes';
import { uploadAssetFile } from '@/utils/uploadAssetFile';
import {
  fitInputs,
  hasSlot,
  inputKind,
  isReady,
  MEDIA_TYPES,
  mediaTypeOf,
  overLimit,
  referenceRoom,
  toRunInputs,
  typesFor,
  type ComposerInput,
  type FitReason,
  type InputSlot,
} from './inputRules';
import type { GenerateMediaType, GenerateModel } from '@/api/generate.api';
import type { MediaAsset } from '@/types/api/assets';

const logger = log.getLogger('useComposerInputs');

/** A message about inputs, shown over the prompt box until it is closed or replaced. */
export type InputNotice =
  /** `fromPicker` is a pick in the computer's file picker, which has its own message. */
  | { type: 'limit'; limit: 'total' | 'video'; fromPicker: boolean }
  | { type: 'uploadFailed' }
  /** `formats` is set when every file left out is an image in a format the canvas doesn't take, such as GIF. */
  | { type: 'wrongType'; count: number; formats: boolean }
  /** Images left over after each frame got one. */
  | { type: 'frameTakesOne'; count: number }
  | { type: 'notTaken'; modelName: string };

/** A slot the user opened, or a drop on the prompt box, which picks the slot itself. */
export type InputTarget = InputSlot | 'drop';

/**
 * A new item before it gets a place: its type, and its asset when it needs no upload. `otherImageFormat` marks
 * an image file in a format the canvas doesn't take, which has no media type.
 */
type NewItem = {
  mediaType: GenerateMediaType | undefined;
  asset?: MediaAsset;
  otherImageFormat?: boolean;
  previewUrl?: string;
};

const FRAME_SLOTS: readonly InputSlot[] = ['firstFrame', 'lastFrame'];

const fileMediaType = (file: File): GenerateMediaType | undefined =>
  MEDIA_TYPES.find((mediaType) => fileMatchesAcceptedCategory(file, mediaType));

/** The same rule as the agent chat: a local picture for images and videos, except HEIC, which browsers can't draw. */
const BROWSER_UNREADABLE_IMAGE_TYPES = new Set(['image/heic', 'image/heif']);
const previewUrlFor = (file: File, mediaType: GenerateMediaType | undefined): string | undefined =>
  (mediaType === 'image' || mediaType === 'video') && !BROWSER_UNREADABLE_IMAGE_TYPES.has(file.type)
    ? URL.createObjectURL(file)
    : undefined;

const noticeFor = (reason: FitReason, model: GenerateModel): InputNotice | null => {
  switch (reason) {
    case 'none':
      return null;
    case 'limit':
      return { type: 'limit', limit: 'total', fromPicker: false };
    case 'videoLimit':
      return { type: 'limit', limit: 'video', fromPicker: false };
    case 'notTaken':
      return { type: 'notTaken', modelName: model.name };
  }
};

/**
 * The references and frames in the prompt box, in the order the user added them, and the rules for them: what
 * each model takes, its limit, and what a model switch keeps. A file takes its place when the user adds it, and
 * gets its asset when its upload ends. An upload whose place was removed meanwhile, by a switch or by another
 * frame put in its slot, is dropped when it ends.
 */
export function useComposerInputs(model: GenerateModel | null) {
  const [attached, setAttachedState] = useState<ComposerInput[]>([]);
  const [notice, setNotice] = useState<InputNotice | null>(null);
  // Uploads end at any time, so they read the current inputs and model from here, not from an old render.
  const current = useRef({ attached, model });
  const lastModel = useRef(model);

  // Every change goes through here, so a local picture is let go once its input leaves or gets its asset.
  const setAttached = useCallback((next: ComposerInput[]) => {
    const keptPreviews = new Set(next.map((input) => input.previewUrl));
    for (const input of current.current.attached) {
      if (input.previewUrl && !keptPreviews.has(input.previewUrl)) {
        URL.revokeObjectURL(input.previewUrl);
      }
    }
    current.current.attached = next;
    setAttachedState(next);
  }, []);

  useEffect(
    () => () => {
      for (const input of current.current.attached) {
        if (input.previewUrl) {
          URL.revokeObjectURL(input.previewUrl);
        }
      }
    },
    [],
  );

  useEffect(() => {
    current.current.model = model;
    const from = lastModel.current;
    if (!model) {
      return;
    }
    lastModel.current = model;
    // The same model can lose a frame slot once its definition loads, if the definition doesn't list that key.
    if (!from || (from.slug === model.slug && isEqual(from.inputs, model.inputs))) {
      return;
    }
    if (inputKind(from) !== inputKind(model)) {
      setAttached([]);
      setNotice(null);
      return;
    }
    const { kept, reason } = fitInputs(current.current.attached, model.inputs);
    setAttached(kept);
    if (reason !== 'none') {
      setNotice(noticeFor(reason, model));
    }
  }, [model, setAttached]);

  /**
   * Gives each new item its place, or none, and sets the message. A drop or pick past the limit adds nothing.
   * Items of a type the slot doesn't take are left out while the rest go in. A frame replaces what its slot had,
   * and images left over once each frame has one are left out too.
   */
  const add = useCallback(
    (items: readonly NewItem[], target: InputTarget, fromPicker: boolean): (ComposerInput | undefined)[] => {
      const { model: currentModel, attached: currentAttached } = current.current;
      const none = items.map((): ComposerInput | undefined => undefined);
      // A drop of results that are all attached already changes nothing, and leaves any message showing.
      if (!currentModel || items.length === 0) {
        return none;
      }
      const inputs = currentModel.inputs;
      const slot: InputSlot = target === 'drop' ? (inputs.references ? 'reference' : 'firstFrame') : target;
      if (!hasSlot(inputs, slot)) {
        return none;
      }
      const accepts = (mediaType: GenerateMediaType | undefined): mediaType is GenerateMediaType =>
        mediaType !== undefined && typesFor(inputs, slot).includes(mediaType);
      const wrongItems = items.filter((item) => !accepts(item.mediaType));
      const wrong = wrongItems.length;
      const wrongNotice: InputNotice | null =
        wrong > 0
          ? {
              type: 'wrongType',
              count: wrong,
              formats: typesFor(inputs, slot).includes('image') && wrongItems.every((item) => item.otherImageFormat),
            }
          : null;

      let slots: (InputSlot | undefined)[];
      if (slot === 'reference') {
        const accepted = items.flatMap((item) => (accepts(item.mediaType) ? [item.mediaType] : []));
        const blocked = overLimit(referenceRoom(inputs, currentAttached), accepted);
        if (blocked) {
          setNotice({ type: 'limit', limit: blocked === 'limit' ? 'total' : 'video', fromPicker });
          return none;
        }
        slots = items.map((item) => (accepts(item.mediaType) ? slot : undefined));
      } else {
        // A drop on the box fills the empty frames in order. A frame slot the user opened or dropped on takes one
        // image and replaces what it had.
        const taken = new Set(currentAttached.map((input) => input.slot));
        const open: InputSlot[] =
          target === 'drop' ? FRAME_SLOTS.filter((frame) => hasSlot(inputs, frame) && !taken.has(frame)) : [slot];
        // Next to references a new first frame takes one of their places.
        const needsPlace = inputs.references && open.length > 0 && !taken.has(open[0]);
        if (
          needsPlace &&
          items.some((item) => accepts(item.mediaType)) &&
          referenceRoom(inputs, currentAttached).total <= 0
        ) {
          setNotice({ type: 'limit', limit: 'total', fromPicker });
          return none;
        }
        slots = items.map((item) => (accepts(item.mediaType) ? open.shift() : undefined));
      }
      const leftOver = items.filter((item, index) => accepts(item.mediaType) && !slots[index]).length;
      setNotice(leftOver > 0 ? { type: 'frameTakesOne', count: leftOver } : wrongNotice);

      const placed = items.map((item, index): ComposerInput | undefined => {
        const itemSlot = slots[index];
        return itemSlot && item.mediaType
          ? {
              id: crypto.randomUUID(),
              slot: itemSlot,
              mediaType: item.mediaType,
              asset: item.asset,
              previewUrl: item.previewUrl,
            }
          : undefined;
      });
      const added = placed.filter((input): input is ComposerInput => input !== undefined);
      if (added.length > 0) {
        const replaced = new Set<InputSlot>(added.map((input) => input.slot).filter((frame) => frame !== 'reference'));
        setAttached([...currentAttached.filter((input) => !replaced.has(input.slot)), ...added]);
      }
      return placed;
    },
    [setAttached],
  );

  /**
   * Adds gallery results. A result already used as a reference is skipped, so it never takes two places, and one
   * already in a frame slot changes nothing there. The same result can still be the first frame, the last frame
   * and a reference, for example to make a video that loops.
   */
  const addAssets = useCallback(
    (assets: readonly MediaAsset[], target: InputTarget) => {
      const { model: currentModel, attached: currentAttached } = current.current;
      // a drop on a frames model fills empty frames, so nothing it has can repeat
      const slot = target === 'drop' ? (currentModel?.inputs.references ? 'reference' : undefined) : target;
      const known = new Set(
        currentAttached.flatMap((input) => (input.slot === slot && input.asset ? [input.asset.id] : [])),
      );
      const fresh = assets.filter((asset) => !known.has(asset.id));
      add(
        fresh.map((asset) => ({ mediaType: mediaTypeOf(asset.type), asset })),
        target,
        false,
      );
    },
    [add],
  );

  /** Uploads files from the computer. Each shows at once with its local picture, and gets its asset when it lands. */
  const addFiles = useCallback(
    (files: readonly File[], target: InputTarget, fromPicker: boolean) => {
      const items = files.map((file) => {
        const mediaType = fileMediaType(file);
        return {
          mediaType,
          otherImageFormat: !mediaType && file.type.startsWith('image/'),
          previewUrl: previewUrlFor(file, mediaType),
        };
      });
      const placed = add(items, target, fromPicker);
      files.forEach((file, index) => {
        const input = placed[index];
        if (!input) {
          const unused = items[index].previewUrl;
          if (unused) {
            URL.revokeObjectURL(unused);
          }
          return;
        }
        uploadAssetFile(file, RunType.WorkflowExecution).then(
          (asset) => {
            const inputs = current.current.attached;
            if (inputs.some((other) => other.id === input.id)) {
              setAttached(
                inputs.map((other) => (other.id === input.id ? { ...other, asset, previewUrl: undefined } : other)),
              );
            }
          },
          (error: unknown) => {
            const inputs = current.current.attached;
            if (!inputs.some((other) => other.id === input.id)) {
              return;
            }
            logger.error('uploadAssetFile(): error', error);
            setAttached(inputs.map((other) => (other.id === input.id ? { ...other, failed: true } : other)));
            setNotice({ type: 'uploadFailed' });
          },
        );
      });
    },
    [add, setAttached],
  );

  const remove = useCallback(
    (input: ComposerInput) => setAttached(current.current.attached.filter((other) => other.id !== input.id)),
    [setAttached],
  );

  const clearNotice = useCallback(() => setNotice(null), []);

  const inputs = model?.inputs ?? {};
  const ready = attached.filter(isReady);
  return {
    /** Every input, also while it uploads or after its upload failed. */
    attached,
    room: referenceRoom(inputs, attached),
    /**
     * Generate waits while a file uploads, since each output reads the same assets, and while a failed one is
     * still there, as the agent chat waits to send.
     */
    blocking: ready.length < attached.length,
    /** The models refuse a last frame on its own. */
    firstFrameNeeded:
      ready.some((input) => input.slot === 'lastFrame') && !attached.some((input) => input.slot === 'firstFrame'),
    hasFrame: ready.some((input) => input.slot !== 'reference'),
    runInputs: toRunInputs(inputs, attached),
    notice,
    clearNotice,
    addAssets,
    addFiles,
    remove,
  };
}
